import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  BoardShell,
  ChartTooltip,
  KpiGrid,
  LEGEND,
  NoteList,
  Panel,
  Section,
  signedPct,
  type KpiItem,
} from '@/components/qxo/qxoBoardPrimitives';
import { useQxoUniverse, type UniverseSnapshot } from '@/components/qxo/qxoUniverse';

function last<T>(rows: T[]): T | undefined {
  return rows.length ? rows[rows.length - 1] : undefined;
}

function yoyText(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return signedPct(n);
}

function kpis(data: UniverseSnapshot): KpiItem[] {
  const oc = last(data.oc);
  const cmp = last(data.compare);
  return [
    {
      id: 'oc-roof',
      label: 'OC roofing sales',
      value: oc?.roofing != null ? `$${oc.roofing.toFixed(0)}M` : '—',
      hint: `${oc?.period ?? ''} · ${yoyText(oc?.roofingYoy)} vs last year · shingle mill`,
      tone: (oc?.roofingYoy ?? 0) < 0 ? 'warning' : 'neutral',
    },
    {
      id: 'oc-ins',
      label: 'OC insulation sales',
      value: oc?.insulation != null ? `$${oc.insulation.toFixed(0)}M` : '—',
      hint: `${yoyText(oc?.insulationYoy)} · mill-side read on the TopBuild book`,
      tone: 'neutral',
    },
    {
      id: 'csl',
      label: 'Carlisle CCM sales',
      value: cmp?.cslCcm != null ? `$${cmp.cslCcm.toFixed(0)}M` : '—',
      hint: `${cmp?.period ?? ''} · ${yoyText(cmp?.cslCcmYoy)} · commercial roofing, not shingles`,
      tone: (cmp?.cslCcmYoy ?? 0) > 0 ? 'success' : 'warning',
    },
    {
      id: 'doors',
      label: 'OC doors sales',
      value: oc?.doors != null ? `$${oc.doors.toFixed(0)}M` : '—',
      hint: `${yoyText(oc?.doorsYoy)} · complementary exterior, acquired May 2024`,
      tone: (oc?.doorsYoy ?? 0) < 0 ? 'warning' : 'neutral',
    },
  ];
}

function UniverseBody({ data }: { data: UniverseSnapshot }) {
  const ocLatest = last(data.oc);
  const cmpLatest = last(data.compare);
  return (
    <>
      <KpiGrid items={kpis(data)} columns={4} />
      <Section title="Two different roofs">
        Owens Corning’s shingle business and Carlisle’s commercial roofing business do not move together.
        Insulation is the third line: it is the product TopBuild installs, and it is not in QXO’s Q2 print.
        Dollars are FMP’s reading of each company’s product-segment footnote.
      </Section>
      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 xl:grid-cols-2">
        <Panel
          tall
          title="Owens Corning: shingles, insulation, doors"
          unit="Net sales, $ millions"
          takeaway={`${ocLatest?.period ?? 'Latest'}: roofing ${yoyText(ocLatest?.roofingYoy)} at $${ocLatest?.roofing?.toFixed(0) ?? '—'}M, insulation ${yoyText(ocLatest?.insulationYoy)} at $${ocLatest?.insulation?.toFixed(0) ?? '—'}M, doors ${yoyText(ocLatest?.doorsYoy)}.`}
          note="Doors was acquired in May 2024, so the first quarters are a stub and the year-ago comparison is not a demand rate. These are Owens Corning’s sales, not QXO’s purchases."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.oc} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="" />
              <Tooltip content={<ChartTooltip formatValue={(value) => `$${value.toFixed(0)}M`} />} />
              <Legend {...LEGEND} />
              <Line type="monotone" dataKey="roofing" name="Roofing" stroke="#38bdf8" strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" dataKey="insulation" name="Insulation" stroke="#fbbf24" strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" dataKey="doors" name="Doors" stroke="#fb7185" strokeWidth={2} dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel
          tall
          title="Shingles vs insulation vs commercial roofing"
          unit="Percent vs the same quarter last year"
          takeaway={`Steep-slope mill dollars were ${yoyText(cmpLatest?.ocRoofingYoy)}. Insulation was ${yoyText(cmpLatest?.ocInsulationYoy)}. Carlisle’s commercial roofing was ${yoyText(cmpLatest?.cslCcmYoy)}. QXO’s steep-slope stores were −5.8% in the same quarter.`}
          note="Carlisle’s line uses the Construction Materials segment, renamed Carlisle Construction Materials in 2025. A missing marker is a quarter FMP did not split out, not a zero."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.compare} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <Legend {...LEGEND} />
              <Line type="monotone" dataKey="ocRoofingYoy" name="OC roofing" stroke="#38bdf8" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="ocInsulationYoy" name="OC insulation" stroke="#fbbf24" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="cslCcmYoy" name="Carlisle commercial roofing" stroke="#86efac" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <div className="px-4 pb-4">
        <NoteList
          title="What the calls add"
          subtitle="Read from the FMP transcripts. QXO does not have one. Each note is a fact the 10-Q does not contain."
          items={data.evidence.map((item) => ({
            head: item.head,
            body: item.body,
            foot: `${item.when} · ${item.foot}`,
            badge: item.badge,
          }))}
        />
      </div>
    </>
  );
}

export function QxoUniverseDesk() {
  const state = useQxoUniverse();
  return (
    <BoardShell
      id="universe"
      title="QXO"
      subtitle="What the rest of the industry reported"
      blurb="Segment sales and earnings-call remarks from the public mills and installers that sell into, compete with, or now sit inside QXO. FMP has no cross-company transcript search, so this is a direct read of those filings and calls, not a keyword scrape of the whole market."
      asOf={state.status === 'ready' ? `FMP segments ${new Date(state.data.generatedAt).toLocaleDateString()}` : 'FMP segments'}
      sourceLine="OC, CSL, BLD, IBP via FMP"
      footer={
        state.status === 'ready'
          ? state.data.coverageNote
          : 'Source: FMP revenue-product-segmentation and earning-call-transcript, cached under persistent_analyses/QXO/universe.'
      }
    >
      {state.status === 'loading' ? (
        <p className="px-5 py-8 text-[13px] text-sky-300">Pulling peer segment filings…</p>
      ) : state.status === 'error' ? (
        <div className="px-5 py-8 text-[13px] text-rose-300">
          <p>Industry universe unavailable: {state.error}</p>
          <p className="mt-1 text-sky-400">Start the backend with start_backend.sh. The other boards do not depend on this one.</p>
        </div>
      ) : (
        <UniverseBody data={state.data} />
      )}
    </BoardShell>
  );
}
